import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { useBranchStore } from '../../stores/branch.store';
import { useAuthStore } from '../../stores/auth.store';
import { useOrders } from '../../api/queries/order.queries';
import type { OrderFilters } from '../../api/queries/order.queries';
import OrdersTable from './components/OrdersTable';
import OrderDetailsModal from './components/OrderDetailsModal';
import type { Order } from '../../api/queries/order.queries';

function OrdersPage() {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const selectedBranchId = useBranchStore((state) => state.selectedBranchId);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  // Filter states
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState<string>('');
  const [dateFilter, setDateFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const branchId = selectedBranchId || user?.branchId;

  // Build filters object
  const getFilters = (): OrderFilters => {
    const filters: OrderFilters = {};

    if (statusFilter) {
      filters.status = statusFilter;
    }

    if (paymentFilter === 'paid') {
      filters.isPaidInFull = true;
    } else if (paymentFilter === 'pending') {
      filters.isPaidInFull = false;
    }

    if (searchQuery.trim()) {
      filters.search = searchQuery.trim();
    }

    // Date presets
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (dateFilter === 'today') {
      filters.startDate = today.toISOString().split('T')[0];
      filters.endDate = today.toISOString().split('T')[0];
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      filters.startDate = yesterday.toISOString().split('T')[0];
      filters.endDate = yesterday.toISOString().split('T')[0];
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      filters.startDate = weekAgo.toISOString().split('T')[0];
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filters.startDate = monthAgo.toISOString().split('T')[0];
    }

    return filters;
  };

  const { data, isLoading } = useOrders(branchId, getFilters(), page, limit);

  const orders = data?.orders || [];
  const pagination = data?.pagination;

  const statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'new', label: 'New' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'ready', label: 'Ready' },
    { value: 'picked_up', label: 'Picked Up' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'voided', label: 'Voided' },
  ];

  const paymentOptions = [
    { value: '', label: 'All Payments' },
    { value: 'paid', label: 'Paid' },
    { value: 'pending', label: 'Pending' },
  ];

  const dateOptions = [
    { value: '', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'week', label: 'Last 7 Days' },
    { value: 'month', label: 'Last 30 Days' },
  ];

  const resetPage = () => setPage(1);

  const clearAllFilters = () => {
    setStatusFilter('');
    setPaymentFilter('');
    setDateFilter('');
    setSearchQuery('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter || paymentFilter || dateFilter || searchQuery;

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="border-b bg-card p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{t('orders.title') || 'Orders'}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {pagination?.total || 0} total orders
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search order #..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  resetPage();
                }}
                className="rounded-lg border bg-background pl-9 pr-4 py-2 w-[180px] text-sm"
              />
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                resetPage();
              }}
              className="rounded-lg border bg-background px-3 py-2 text-sm"
            >
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Payment Filter */}
            <select
              value={paymentFilter}
              onChange={(e) => {
                setPaymentFilter(e.target.value);
                resetPage();
              }}
              className="rounded-lg border bg-background px-3 py-2 text-sm"
            >
              {paymentOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                resetPage();
              }}
              className="rounded-lg border bg-background px-3 py-2 text-sm"
            >
              {dateOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-1 px-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                title="Clear all filters"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <ShoppingBag className="h-12 w-12 animate-pulse text-muted-foreground" />
          </div>
        ) : !branchId ? (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <ShoppingBag className="h-16 w-16 mb-4 opacity-20" />
            <p className="text-lg font-medium">Please select a branch</p>
            <p className="text-sm">Use the branch selector to view orders</p>
          </div>
        ) : orders.length > 0 ? (
          <OrdersTable orders={orders} onViewDetails={setSelectedOrder} />
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <ShoppingBag className="h-16 w-16 mb-4 opacity-20" />
            <p>{t('orders.noOrders') || 'No orders found'}</p>
            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="border-t bg-card p-4 flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.pages} ({pagination.total} orders)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border bg-background hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
              disabled={page === pagination.pages}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg border bg-background hover:bg-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
        />
      )}
    </div>
  );
}

export default OrdersPage;
